import { Box, Grid, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { CLOSED_STATUS, OPEN_STATUS, statusOptions } from '../../constants/businessHours';
const PREFIX = 'EditBusinessHours';

const classes = {
  container: `${PREFIX}-container`,
  typographyPadding: `${PREFIX}-typographyPadding`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },

  [`& .${classes.typographyPadding}`]: {
    padding: theme.spacing(1, 2),
  },
}));

const EditBusinessHours = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { control, setValue, watch, getValues } = useFormContext(); // Use useFormContext to access parent form's control

  const { fields } = useFieldArray({
    control,
    name: 'business_hours', // The nested field name in the parent form
  });

  const watchFieldArray = watch('business_hours');
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const validateTime = (open, close) => {
    return new Date(`1970-01-01T${open}`) < new Date(`1970-01-01T${close}`);
  };

  return (
    <StyledBox className={classes.container}>
      {controlledFields.map((dayField, index) => (
        <Grid key={`business-hours-${index}`} container alignItems="center" spacing={{ xs: 1, sm: 2 }}>
          <Grid size={{ xs: 12, sm: 2, md: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {dayField.day}:
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 2, md: 2 }}>
            <Controller
              name={`business_hours.${index}.status`}
              control={control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  select
                  fullWidth
                  {...field}
                  onChange={(e) => {
                    setValue(`business_hours.${index}.open`, '');
                    setValue(`business_hours.${index}.close`, '');
                    field.onChange(e);
                  }}
                  size="small"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <Box style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Controller
                name={`business_hours.${index}.open`}
                control={control}
                rules={{
                  validate: (value) => {
                    if (dayField?.status === OPEN_STATUS && !value) {
                      return 'Opening time is required';
                    }

                    const toValue = getValues(`business_hours.${index}.close`);
                    return dayField?.status === CLOSED_STATUS || validateTime(value, toValue) || 'Opening time must be before Closing time';
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <TextField
                      type="time"
                      {...field}
                      disabled={dayField?.status === CLOSED_STATUS}
                      size="small"
                      error={!!fieldState.error}
                      variant={'outlined'}
                      fullWidth
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{
                        input: {
                          endAdornment: isSmallScreen ? <InputAdornment position="end"></InputAdornment> : null,
                        },

                        htmlInput: {
                          step: 300,
                          max: getValues(`business_hours.${index}.close`),
                        },
                      }}
                    />
                  );
                }}
              />
              <Typography variant="body1" className={classes.typographyPadding}>
                to
              </Typography>
              <Controller
                name={`business_hours.${index}.close`}
                control={control}
                rules={{
                  validate: (value, fieldState) => {
                    const openValue = getValues(`business_hours.${index}.open`);
                    if (dayField?.status === OPEN_STATUS && !value) {
                      return 'Closing time is required';
                    }

                    return dayField?.status === CLOSED_STATUS || validateTime(openValue, value) || 'Closing time must be after Opening time';
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    type="time"
                    {...field}
                    disabled={dayField?.status === CLOSED_STATUS}
                    error={!!fieldState.error}
                    helperText={fieldState.error ? fieldState.error.message : null}
                    variant={'outlined'}
                    size="small"
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: isSmallScreen ? <InputAdornment position="end"></InputAdornment> : null,
                      },

                      htmlInput: {
                        step: 300,
                        min: getValues(`business_hours.${index}.open`),
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      ))}
    </StyledBox>
  );
};

export default EditBusinessHours;
